import { useState, useEffect } from 'react';
import { Link2, ExternalLink, Plus, Calendar, FileText, TrendingUp, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LinkBuilding({ user, selectedClient }) {
  const [backlinkUpdates, setBacklinkUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  const [newUpdate, setNewUpdate] = useState({
    title: '',
    google_sheet_link: '',
    backlinks_added: '',
    notes: ''
  });

  const firmId = selectedClient?.firmId || user?.firm_id;
  const canEdit = user?.role && ['super_admin', 'internal_team'].includes(user.role);
  const isClient = user?.role === 'client';

  useEffect(() => {
    if (firmId) {
      fetchBacklinkUpdates();
    }
  }, [firmId]);

  const fetchBacklinkUpdates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/backlink-updates?firm_id=${firmId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBacklinkUpdates(response.data || []);
    } catch (err) {
      console.error('Error fetching backlink updates:', err);
      setBacklinkUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    
    if (!newUpdate.title || !newUpdate.google_sheet_link || !newUpdate.backlinks_added) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/backlink-updates`, {
        firm_id: firmId,
        title: newUpdate.title,
        google_sheet_link: newUpdate.google_sheet_link,
        backlinks_added: parseInt(newUpdate.backlinks_added),
        notes: newUpdate.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubmitSuccess(true);
      setNewUpdate({ title: '', google_sheet_link: '', backlinks_added: '', notes: '' });
      setShowAddModal(false);
      fetchBacklinkUpdates();
      
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err) {
      console.error('Error creating backlink update:', err);
      setSubmitError(err.response?.data?.detail || 'Failed to create backlink update');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate total backlinks from all updates
  const totalBacklinks = backlinkUpdates.reduce((sum, update) => sum + (update.backlinks_added || 0), 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  return (
    <div className="space-y-6" data-testid="link-building-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
            Link Building
          </h1>
          <p className="text-slate-600">Track backlink batches and domain authority growth</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300"
            data-testid="add-backlink-update-btn"
          >
            <Plus className="w-4 h-4" />
            Add Backlink Update
          </button>
        )}
      </div>

      {/* Status Messages */}
      {submitSuccess && (
        <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>Backlink update added successfully!</span>
        </div>
      )}

      {submitError && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{submitError}</span>
        </div>
      )}

      {/* Summary Card - Total Backlinks Built */}
      <div className="bg-gradient-to-r from-violet-500 to-indigo-600 rounded-2xl p-6 text-white soft-shadow-lg" data-testid="total-backlinks-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Link2 className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-white/80">Total Backlinks Built</h3>
              <div className="text-4xl font-bold" style={{fontFamily: 'Outfit'}}>{totalBacklinks}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-white/60">From {backlinkUpdates.length} batches</div>
            <div className="text-sm text-white/80 mt-1">
              <TrendingUp className="w-4 h-4 inline mr-1" />
              Cumulative total
            </div>
          </div>
        </div>
      </div>

      {/* Backlink Updates Table */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden soft-shadow">
        <div className="p-5 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Backlink Updates</h3>
          <p className="text-sm text-slate-500">History of all backlink batches added</p>
        </div>
        
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-slate-500">Loading backlink updates...</p>
          </div>
        ) : backlinkUpdates.length === 0 ? (
          <div className="p-12 text-center">
            <Link2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Backlink Updates Yet</h3>
            <p className="text-slate-500">
              {canEdit ? 'Click "Add Backlink Update" to add your first batch.' : 'Backlink updates will appear here once added.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Update Title</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Backlinks Added</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Google Sheet</th>
                  {canEdit && (
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Notes</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {backlinkUpdates.map((update) => (
                  <tr key={update.id} className="hover:bg-slate-50" data-testid={`backlink-update-row-${update.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                          <FileText className="w-4 h-4 text-violet-600" />
                        </div>
                        <span className="font-medium text-slate-900">{update.title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {formatDate(update.date_created)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold">
                        +{update.backlinks_added}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <a
                        href={update.google_sheet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                        data-testid={`view-sheet-${update.id}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                        View
                      </a>
                    </td>
                    {canEdit && (
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-500 truncate max-w-xs block">
                          {update.notes || '-'}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Backlink Update Modal */}
      {showAddModal && canEdit && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 soft-shadow-lg animate-fadeInUp">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>
              Add Backlink Update
            </h2>
            
            <form onSubmit={handleSubmitUpdate} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Update Title *</label>
                <input
                  type="text"
                  value={newUpdate.title}
                  onChange={(e) => setNewUpdate({...newUpdate, title: e.target.value})}
                  required
                  placeholder="e.g., March 2026 Backlink Batch"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                  data-testid="update-title-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Google Sheet Link *</label>
                <input
                  type="url"
                  value={newUpdate.google_sheet_link}
                  onChange={(e) => setNewUpdate({...newUpdate, google_sheet_link: e.target.value})}
                  required
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                  data-testid="google-sheet-link-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Number of Backlinks Created *</label>
                <input
                  type="number"
                  min="1"
                  value={newUpdate.backlinks_added}
                  onChange={(e) => setNewUpdate({...newUpdate, backlinks_added: e.target.value})}
                  required
                  placeholder="e.g., 12"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                  data-testid="backlinks-count-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={newUpdate.notes}
                  onChange={(e) => setNewUpdate({...newUpdate, notes: e.target.value})}
                  rows={3}
                  placeholder="e.g., Guest posts and niche edits"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                  data-testid="notes-input"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSubmitError(null);
                  }}
                  className="px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all disabled:opacity-50"
                  data-testid="submit-update-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                      Adding...
                    </>
                  ) : (
                    'Add Update'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
